import { useCallback, useRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Tag, Modal, message, Select, Alert } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getTenants,
  deleteTenant,
  type Tenant,
  type TenantFilters,
} from '@/api/adminApi';
import { useAuth } from '@/hooks/useAuth';
import AdminNav from './AdminNav';

const PLANS = ['free', 'starter', 'professional', 'enterprise'];
const STATUSES = ['active', 'suspended', 'cancelled'];

const PLAN_COLORS: Record<string, string> = {
  free: 'default',
  starter: 'blue',
  professional: 'green',
  enterprise: 'gold',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  suspended: 'orange',
  cancelled: 'red',
};

export default function TenantListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [planFilter, setPlanFilter] = useState<string | undefined>();

  const canRead = hasPermission('admin', 'read');
  const canWrite = hasPermission('admin', 'write');
  const canDelete = hasPermission('admin', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: TenantFilters = { ...params };
      if (statusFilter) filters.status = statusFilter;
      if (planFilter) filters.plan = planFilter;
      return getTenants(filters);
    },
    [statusFilter, planFilter]
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('admin.confirm_delete_tenant'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteTenant(id);
            message.success(t('admin.tenant_delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<Tenant> = useMemo(
    () => [
      {
        title: t('admin.field.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true,
        ellipsis: true,
      },
      {
        title: t('admin.field.domain'),
        dataIndex: 'domain',
        key: 'domain',
        sorter: true,
        ellipsis: true,
      },
      {
        title: t('admin.field.plan'),
        dataIndex: 'plan',
        key: 'plan',
        width: 120,
        render: (plan: string) => (
          <Tag color={PLAN_COLORS[plan] ?? 'default'}>
            {t(`admin.plan_options.${plan}`, plan)}
          </Tag>
        ),
      },
      {
        title: t('admin.field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (status: string) => (
          <Tag color={STATUS_COLORS[status] ?? 'default'}>
            {t(`admin.status_options.${status}`, status)}
          </Tag>
        ),
      },
      {
        title: t('admin.field.contact_email'),
        dataIndex: 'contact_email',
        key: 'contact_email',
        ellipsis: true,
      },
      {
        title: t('admin.field.max_users'),
        dataIndex: 'max_users',
        key: 'max_users',
        width: 100,
        sorter: true,
      },
      {
        title: t('admin.field.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        width: 160,
        sorter: true,
        render: (v: string) => v ? new Date(v).toLocaleString() : '-',
      },
      {
        title: t('app.actions'),
        key: 'col_actions',
        width: 140,
        render: (_: unknown, record: Tenant) => (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/admin/tenants/${record.id}`)}
            />
            {canWrite && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/admin/tenants/${record.id}/edit`)}
              />
            )}
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete]
  );

  const toolbar = (
    <Space>
      <Select
        allowClear
        placeholder={t('admin.field.status')}
        value={statusFilter}
        onChange={(v) => { setStatusFilter(v); gridRef.current?.reload(); }}
        style={{ width: 140 }}
        options={STATUSES.map((s) => ({ value: s, label: t(`admin.status_options.${s}`) }))}
      />
      <Select
        allowClear
        placeholder={t('admin.field.plan')}
        value={planFilter}
        onChange={(v) => { setPlanFilter(v); gridRef.current?.reload(); }}
        style={{ width: 140 }}
        options={PLANS.map((p) => ({ value: p, label: t(`admin.plan_options.${p}`) }))}
      />
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/admin/tenants/new')}
        >
          {t('admin.create_tenant_title')}
        </Button>
      )}
    </Space>
  );

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;

  return (
    <div>
      <AdminNav />
      <h2>{t('admin.tenants_title')}</h2>
      <DataGrid<Tenant>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('admin.search_tenants_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
      />
    </div>
  );
}
